// Direct GraphQL query string (no need to import since we're using fetch directly)
const FUSION_MANAGE_PICKLIST_QUERY_STRING = `
  query FusionManagePicklist($lookupName: String!) {
    fusionManagePicklist(lookupName: $lookupName) {
      lookupName
      items {
        id
        value
        label
        name
        displayName
        picklistId
      }
    }
  }
`;

/**
 * Fetch a picklist from Fusion Manage
 * @param {String} lookupName - The lookup name (e.g., 'CUSTOM_LOOKUP_QA_ISSUE_TYPE')
 * @returns {Promise<Array>} - Array of picklist items formatted for ReactSelect
 */
export const fetchFusionManagePicklist = async (lookupName) => {
  const startTime = Date.now();
  console.log(`[fusionManageUtils] Fetching picklist: ${lookupName}`);

  try {
    const response = await fetch('/api/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: FUSION_MANAGE_PICKLIST_QUERY_STRING,
        variables: {
          lookupName: lookupName
        }
      }),
    });

    if (!response.ok) {
      console.error(`[fusionManageUtils] HTTP ${response.status} ${response.statusText} when fetching picklist "${lookupName}"`);
      console.error(`[fusionManageUtils] This may indicate an authentication or authorization issue with Fusion Manage.`);
      throw new Error(`Fusion Manage picklist fetch failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (data.errors) {
      console.warn(`[fusionManageUtils] GraphQL errors fetching picklist "${lookupName}":`, data.errors);
      console.warn(`[fusionManageUtils] Possible causes:`);
      console.warn(`[fusionManageUtils]   1. Lookup "${lookupName}" does not exist in Fusion Manage`);
      console.warn(`[fusionManageUtils]   2. OAuth token expired or invalid`);
      console.warn(`[fusionManageUtils]   3. Workspace permissions issue`);
      console.warn(`[fusionManageUtils] Please check Fusion Manage admin panel to verify lookup exists and has proper permissions.`);
      return [];
    }

    const items = data.data?.fusionManagePicklist?.items || [];
    const elapsedTime = Date.now() - startTime;

    // If no items found, log a warning
    if (items.length === 0) {
      console.warn(`[fusionManageUtils] Picklist "${lookupName}" returned 0 items (took ${elapsedTime}ms)`);
      console.warn(`[fusionManageUtils] Please check if this lookup exists and has values configured in Fusion Manage.`);
      console.warn(`[fusionManageUtils] If the lookup should have data, verify workspace permissions and OAuth token validity.`);
    } else {
      console.log(`[fusionManageUtils] Successfully fetched ${items.length} items from "${lookupName}" (took ${elapsedTime}ms)`);
    }

    // Format items for ReactSelect
    return items.map(item => ({
      value: item.id || item.value || item.picklistId || item.name,
      label: item.label || item.displayName || item.name || item.value,
      id: item.id || item.picklistId,
      ...item // Keep all original properties
    }));
  } catch (error) {
    const elapsedTime = Date.now() - startTime;
    console.error(`[fusionManageUtils] Error fetching picklist "${lookupName}" (failed after ${elapsedTime}ms):`, error);
    console.error(`[fusionManageUtils] Form will continue without Fusion Manage data for this picklist.`);
    return [];
  }
};

/**
 * Merge Epicor and Fusion Manage picklists with enhanced deduplication
 * Fusion Manage items take precedence, then Epicor items are added if not already present
 * @param {Array} epicorItems - Items from Epicor
 * @param {Array} fusionItems - Items from Fusion Manage
 * @returns {Array} - Merged array of items without duplicates
 */
/**
 * Fetch workspace schema from Fusion Manage (NEW REST API)
 * @param {String} workspaceId - Workspace ID (e.g., '98', '374')
 * @returns {Promise<Object>} - Schema with fields array
 */
export const fetchWorkspaceSchema = async (workspaceId) => {
  console.log(`[fusionManageUtils] Fetching workspace ${workspaceId} schema`);

  try {
    const response = await fetch(`/api/fm/workspaces/${workspaceId}/schema`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      console.error(`[fusionManageUtils] HTTP ${response.status} when fetching schema for workspace ${workspaceId}`);
      return { fields: [], allFields: [] };
    }

    const data = await response.json();

    if (!data.success) {
      console.error(`[fusionManageUtils] Failed to fetch schema:`, data.error);
      return { fields: [], allFields: [] };
    }

    console.log(`[fusionManageUtils] Successfully fetched schema for workspace ${workspaceId}: ${data.fields.length} fields`);
    return {
      fields: data.fields || [],
      allFields: data.allFields || []
    };
  } catch (error) {
    console.error(`[fusionManageUtils] Error fetching workspace schema:`, error);
    return { fields: [], allFields: [] };
  }
};

/**
 * Fetch picklist options from Fusion Manage (NEW REST API - more reliable than GraphQL)
 * @param {String} lookupName - The lookup name (e.g., 'CUSTOM_LOOKUP_QA_ISSUE_TYPE')
 * @returns {Promise<Array>} - Array of picklist items formatted for ReactSelect
 */
export const fetchFusionManagePicklistDirect = async (lookupName) => {
  const startTime = Date.now();
  console.log(`[fusionManageUtils] Fetching picklist (REST API): ${lookupName}`);

  try {
    const response = await fetch(`/api/fm/picklists/${lookupName}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      console.error(`[fusionManageUtils] HTTP ${response.status} when fetching picklist "${lookupName}"`);
      return [];
    }

    const data = await response.json();

    if (!data.success) {
      console.error(`[fusionManageUtils] Failed to fetch picklist:`, data.error);
      return [];
    }

    const items = data.options || [];
    const elapsedTime = Date.now() - startTime;

    if (items.length === 0) {
      console.warn(`[fusionManageUtils] Picklist "${lookupName}" returned 0 items (took ${elapsedTime}ms)`);
    } else {
      console.log(`[fusionManageUtils] Successfully fetched ${items.length} items from "${lookupName}" (took ${elapsedTime}ms)`);
    }

    // Helper function to clean picklist values (remove trailing spaces/dashes, trim whitespace)
    const cleanPicklistValue = (str) => {
      if (str === null || str === undefined) return '';
      if (typeof str !== 'string') {
        // Convert to string if it's a number or other type
        return String(str).trim();
      }
      return str.trim().replace(/\s+-\s*$/, '').trim(); // Remove trailing " -" or "-" with optional spaces
    };

    // Items are already formatted by the backend, but ensure they have required properties
    const processedItems = items
      .map((item, index) => {
        const cleanedValue = cleanPicklistValue(item.value);
        const cleanedLabel = cleanPicklistValue(item.label);
        // Ensure we have valid value and label - use fallbacks if needed
        const finalValue = cleanedValue || item.id || item.link || String(index);
        const finalLabel = cleanedLabel || cleanedValue || item.title || item.name || item.displayName || finalValue;

        return {
          value: finalValue,
          label: finalLabel,
          id: item.id,
          ...item
        };
      })
      .filter((item) => {
        // More lenient validation - allow any non-empty value and label
        const hasValue = item.value !== null && item.value !== undefined && String(item.value).trim() !== '';
        const hasLabel = item.label !== null && item.label !== undefined && String(item.label).trim() !== '';
        return hasValue && hasLabel;
      });

    return processedItems;
  } catch (error) {
    const elapsedTime = Date.now() - startTime;
    console.error(`[fusionManageUtils] Error fetching picklist "${lookupName}" (failed after ${elapsedTime}ms):`, error);
    return [];
  }
};

/**
 * Normalize a name string for comparison (removes extra spaces, converts to lowercase)
 * @param {String} name - Name to normalize
 * @returns {String} - Normalized name
 */
function normalizeName(name) {
  if (!name || typeof name !== 'string') return '';
  return name.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Extract first and last name from a full name string
 * @param {String} fullName - Full name (e.g., "Storm Hicks", "Storm H.", "Hicks, Storm")
 * @returns {Object} - {firstName, lastName, normalized}
 */
function parseName(fullName) {
  const normalized = normalizeName(fullName);
  if (!normalized) return { firstName: '', lastName: '', normalized: '' };

  // Handle "Last, First" format
  if (normalized.includes(',')) {
    const parts = normalized.split(',').map(p => p.trim());
    return {
      firstName: parts[1] || '',
      lastName: parts[0] || '',
      normalized: normalized
    };
  }

  // Handle "First Last" or "First M. Last" format
  const parts = normalized.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '', normalized: normalized };
  }
  
  return {
    firstName: parts[0] || '',
    lastName: parts[parts.length - 1] || '',
    normalized: normalized
  };
}

/**
 * Check if two names are likely the same person (handles variations)
 * @param {String} name1 - First name
 * @param {String} name2 - Second name
 * @returns {Boolean} - True if names likely refer to the same person
 */
function areNamesSimilar(name1, name2) {
  const norm1 = normalizeName(name1);
  const norm2 = normalizeName(name2);
  
  // Exact match after normalization
  if (norm1 === norm2) return true;
  
  // If one is a substring of the other (handles "Storm Hicks" vs "Storm H.")
  if (norm1.includes(norm2) || norm2.includes(norm1)) {
    // Only consider it a match if the shorter one is at least 5 characters
    // (to avoid false positives like "John" matching "Johnson")
    const shorter = norm1.length < norm2.length ? norm1 : norm2;
    if (shorter.length >= 5) return true;
  }
  
  // Parse names and compare first/last
  const parsed1 = parseName(name1);
  const parsed2 = parseName(name2);
  
  // If both have first and last names, check if they match
  if (parsed1.firstName && parsed1.lastName && parsed2.firstName && parsed2.lastName) {
    // First name matches and last name matches
    if (parsed1.firstName === parsed2.firstName && parsed1.lastName === parsed2.lastName) {
      return true;
    }
    // First name matches and one last name is a prefix of the other (handles "Hicks" vs "H.")
    if (parsed1.firstName === parsed2.firstName) {
      const lastName1 = parsed1.lastName;
      const lastName2 = parsed2.lastName;
      if (lastName1.length >= 2 && lastName2.length >= 2) {
        if (lastName1.startsWith(lastName2) || lastName2.startsWith(lastName1)) {
          return true;
        }
      }
    }
  }
  
  return false;
}

/**
 * Deduplicate items within a single array using smart name matching
 * @param {Array} items - Array of items to deduplicate
 * @returns {Array} - Deduplicated array
 */
function deduplicateItems(items) {
  const seen = new Map(); // Map of normalized keys to items
  const result = [];

  items.forEach(item => {
    const label = item.label || item.value || '';
    const value = item.value || item.id || '';
    
    // Try to find a similar item
    let found = false;
    for (const [key, existingItem] of seen.entries()) {
      const existingLabel = existingItem.label || existingItem.value || '';
      
      // Check exact match first
      if (normalizeName(label) === normalizeName(existingLabel) ||
          normalizeName(value) === normalizeName(existingLabel)) {
        found = true;
        break;
      }
      
      // Check if names are similar (for person names)
      if (areNamesSimilar(label, existingLabel)) {
        found = true;
        // Keep the one with more complete information (longer label)
        if (label.length > existingLabel.length) {
          seen.set(key, item);
        }
        break;
      }
    }
    
    if (!found) {
      const key = normalizeName(label) || normalizeName(value);
      if (key) {
        seen.set(key, item);
        result.push(item);
      }
    }
  });

  return result;
}

/**
 * Merge Epicor and Fusion Manage picklists with enhanced deduplication
 * Fusion Manage items take precedence, then Epicor items are added if not already present
 * @param {Array} epicorItems - Items from Epicor
 * @param {Array} fusionItems - Items from Fusion Manage
 * @returns {Array} - Merged array of items without duplicates
 */
export const mergePicklists = (epicorItems = [], fusionItems = []) => {
  // Log when both sources are empty (helps debugging empty dropdowns)
  if (epicorItems.length === 0 && fusionItems.length === 0) {
    console.warn(`[fusionManageUtils] mergePicklists: Both Epicor and Fusion Manage sources are empty`);
    console.warn(`[fusionManageUtils] This will result in an empty dropdown. Fallback data should be used if available.`);
  }

  // Helper function to clean picklist values (remove trailing spaces/dashes, trim whitespace)
  const cleanPicklistValue = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str.trim().replace(/\s+-\s*$/, '').trim(); // Remove trailing " -" or "-" with optional spaces
  };

  // Clean items before processing
  const cleanedFusionItems = fusionItems.map(item => ({
    ...item,
    value: cleanPicklistValue(item.value),
    label: cleanPicklistValue(item.label) || cleanPicklistValue(item.value)
  }));
  const cleanedEpicorItems = epicorItems.map(item => ({
    ...item,
    value: cleanPicklistValue(item.value),
    label: cleanPicklistValue(item.label) || cleanPicklistValue(item.value)
  }));

  // First, deduplicate each source individually
  const deduplicatedFusion = deduplicateItems(cleanedFusionItems);
  const deduplicatedEpicor = deduplicateItems(cleanedEpicorItems);

  const merged = [...deduplicatedFusion]; // Start with Fusion Manage items

  // Create a Set of Fusion Manage values and labels (normalized for comparison)
  const fusionKeys = new Set();
  const fusionLabels = new Map(); // Map normalized label to item for similarity checking
  
  deduplicatedFusion.forEach(item => {
    const label = item.label || item.value || '';
    const value = item.value || item.id || '';
    const normLabel = normalizeName(label);
    const normValue = normalizeName(value);
    
    if (normLabel) {
      fusionKeys.add(normLabel);
      fusionLabels.set(normLabel, item);
    }
    if (normValue && normValue !== normLabel) {
      fusionKeys.add(normValue);
    }
  });

  // Add Epicor items that don't exist in Fusion Manage
  let epicorAddedCount = 0;
  deduplicatedEpicor.forEach(item => {
    const itemLabel = item.label || item.value || '';
    const itemValue = item.value || item.id || '';
    const normLabel = normalizeName(itemLabel);
    const normValue = normalizeName(itemValue);

    // Check exact matches first
    if (fusionKeys.has(normLabel) || fusionKeys.has(normValue)) {
      return; // Skip this item, it's a duplicate
    }

    // Check for similar names (for person names)
    let isSimilar = false;
    for (const [existingLabel, existingItem] of fusionLabels.entries()) {
      if (areNamesSimilar(itemLabel, existingItem.label || existingItem.value || '')) {
        isSimilar = true;
        break;
      }
    }

    if (!isSimilar) {
      merged.push(item);
      epicorAddedCount++;
      // Add to sets to prevent duplicates within Epicor items
      if (normLabel) {
        fusionKeys.add(normLabel);
        fusionLabels.set(normLabel, item);
      }
      if (normValue && normValue !== normLabel) {
        fusionKeys.add(normValue);
      }
    }
  });

  // Final deduplication pass on merged array
  const finalMerged = deduplicateItems(merged);

  // Log merge statistics for debugging
  if (epicorItems.length > 0 || fusionItems.length > 0) {
    const originalTotal = epicorItems.length + fusionItems.length;
    const duplicatesRemoved = originalTotal - finalMerged.length;
    console.log(`[fusionManageUtils] Merged picklists: ${fusionItems.length} from Fusion, ${epicorItems.length} from Epicor`);
    console.log(`[fusionManageUtils] After deduplication: ${finalMerged.length} unique items (${duplicatesRemoved} duplicates removed)`);
  }

  // Sort by label for better UX
  finalMerged.sort((a, b) => {
    const labelA = (a.label || a.value || '').toLowerCase();
    const labelB = (b.label || b.value || '').toLowerCase();
    return labelA.localeCompare(labelB);
  });

  return finalMerged;
};

